import cloneDeep from 'clone-deep'
import { FieldOrigin, InputType, IVisibleProp, LoanFieldProps } from 'config'
import {
  getFieldOriginOptions,
  getLoanInputFieldOptions,
  InputTypeOfLoans,
  LoanStructureAdditionalPropertyCount,
} from 'config/loan.input.fields.constants'
import { useMemo } from 'react'
import { fillObject } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { canAddLinkedFieldOrigin } from '../inputs'

export interface ILinkedFieldProperties {
  title?: string
  showLinkedField?: boolean
  fieldOrigin?: FieldOrigin[]
}

export const SyncCustomFieldTo = ({
  data,
  setData,
  // fieldOrigin,
  fieldKey,
  fieldTitles,
  fieldInputs,
  properties,
  loanFieldProps,
}: {
  data: IVisibleProp
  setData: Function
  fieldOrigin: FieldOrigin
  fieldKey: string
  fieldTitles: Record<string, { title: string; fields: Record<string, string> }>
  fieldInputs: InputTypeOfLoans
  properties?: ILinkedFieldProperties
  loanFieldProps: LoanFieldProps
}) => {
  // if (fieldKey && !isCustomOrigin(fieldOrigin) && !isCustomFieldKey(fieldKey)) return null

  const loanInputFieldOptions = getLoanInputFieldOptions(
    '',
    {
      isIgnoredField: (fieldOrigin: FieldOrigin, fieldKey: string, input: InputType) => !!(input as any).linkedField,
    },
    fieldInputs,
    fieldTitles,
  )

  const additionalSyncOptions = useMemo(() => {
    if (!loanFieldProps.properties) return {}

    const { additionalProperties } = loanFieldProps.properties[FieldOrigin.LoanStructure]?.additionalInputs
    const additionalPropertyOptions = fillObject(Object.keys(additionalProperties), (key: string) => {
      const { title, linkedField } = additionalProperties[key]
      return title || loanInputFieldOptions[FieldOrigin.LoanStructure][linkedField.fieldKey]
    })

    return {
      [FieldOrigin.LoanStructure]: {
        additionalProperties: {
          title: 'Additional Property Addresses',
          options: fillObject(
            Object.keys(Array.from({ length: LoanStructureAdditionalPropertyCount - 1 })),
            (index: string) => ({
              title: `Additional Property ${Number(index) + 2}`,
              options: additionalPropertyOptions,
            }),
          ),
        },
      },
    }
  }, [data.linkedField])

  type NestedOptionDef = {
    title: string
    options: Record<string, string | NestedOptionDef>
  }

  const inputs = useMemo(() => {
    const inputs: Record<string, InputType> = {}
    const linkedField = (data.linkedField || {}) as Record<string, any>
    const { fieldOrigin: linkedFieldOrigin, fieldKey: linkedFieldKey } = linkedField

    const fieldOptions: Record<string, string> = cloneDeep(getFieldOriginOptions())
    Object.keys(fieldOptions).forEach((fieldOrigin: any) => {
      if (!canAddLinkedFieldOrigin(fieldOrigin as FieldOrigin)) delete fieldOptions[fieldOrigin]
      if (properties?.fieldOrigin && !properties?.fieldOrigin?.includes(fieldOrigin as FieldOrigin))
        delete fieldOptions[fieldOrigin]
    })

    inputs['fieldOrigin'] = {
      inputType: 'select',
      title: 'Page Origin',
      options: fieldOptions,
      hasDefaultOption: true,
      value: linkedFieldOrigin,
      required: true,
      sort: false,
    }

    if (linkedFieldOrigin) {
      const regularOptions = loanInputFieldOptions[linkedFieldOrigin as FieldOrigin] || {}
      const additionalForOrigin =
        (additionalSyncOptions as Record<string, Record<string, NestedOptionDef>>)[linkedFieldOrigin] || {}

      const extraOptions: Record<string, string> = {}
      Object.keys(additionalForOrigin).forEach((key) => {
        extraOptions[key] = additionalForOrigin[key].title
      })

      inputs['fieldKey'] = {
        inputType: 'select',
        title: 'Select Field',
        options: { ...regularOptions, ...extraOptions },
        hasDefaultOption: true,
        value: linkedFieldKey,
        required: true,
        sort: true,
      }

      if (linkedFieldKey && additionalForOrigin[linkedFieldKey]) {
        const addNestedInputs = (optionDef: NestedOptionDef, level: number) => {
          const optionKey = `option${level}`
          const selectedValue = linkedField[optionKey]

          const levelOptions: Record<string, string> = {}
          Object.keys(optionDef.options).forEach((key) => {
            const opt = optionDef.options[key]
            levelOptions[key] = typeof opt === 'string' ? opt : (opt as NestedOptionDef).title
          })

          inputs[optionKey] = {
            inputType: 'select',
            title: optionDef.title,
            options: levelOptions,
            hasDefaultOption: true,
            value: selectedValue,
            required: true,
            sort: false,
          }

          if (selectedValue) {
            const selectedOpt = optionDef.options[selectedValue]
            if (typeof selectedOpt === 'object' && selectedOpt.options) {
              addNestedInputs(selectedOpt as NestedOptionDef, level + 1)
            }
          }
        }

        addNestedInputs(additionalForOrigin[linkedFieldKey], 0)
      }
    }
    return inputs
  }, [data.linkedField, additionalSyncOptions])

  const onChangeControl = (key: string, value: any) => {
    const newData = cloneDeep(data)
    const linkedField = (newData.linkedField || {}) as Record<string, any>

    if (key === 'fieldOrigin') {
      newData.linkedField = { fieldOrigin: value } as any
    } else if (key === 'fieldKey') {
      newData.linkedField = { fieldOrigin: linkedField.fieldOrigin, fieldKey: value } as any
    } else if (/^option\d+$/.test(key)) {
      const level = parseInt(key.replace('option', ''), 10)
      const filtered: Record<string, any> = {}
      Object.keys(linkedField).forEach((k) => {
        if (!/^option\d+$/.test(k)) {
          filtered[k] = linkedField[k]
        } else {
          const kLevel = parseInt(k.replace('option', ''), 10)
          if (kLevel < level) filtered[k] = linkedField[k]
        }
      })
      filtered[key] = value
      newData.linkedField = filtered as any
    } else {
      linkedField[key] = value
      newData.linkedField = linkedField as any
    }

    setData(newData)
  }

  return (
    <div className="mt-4">
      <p className="font-bold text-sm mb-2">{properties?.title || 'Link Field to'}</p>

      <div className="grid md:grid-cols-2 gap-x-2">
        {Object.keys(inputs).map((key) => {
          const elementKey = `${fieldKey}-${key}`
          if (inputs[key].visible === false) return null

          return (
            <div className={`mb-4`} key={elementKey}>
              <RenderInput
                input={inputs[key]}
                Key={key}
                onChange={(_key: string, value: string) => onChangeControl(_key, value)}
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
